import { emitApiMetricSample } from "./metric-emitter";
import { resolveMatchingApiMonitors } from "./helpers";
import type {
  ApiMonitorDefinition,
  ApiRequestContext,
  MonitoringPrimitive,
  PodMonitoringSchema,
} from "./types";

// Fetch instrumentation that records API latency and error-rate samples.
// Stores the original fetch so multiple installers can share one patched wrapper.
interface FetchMonitorGlobals {
  __bflMonitoringOriginalFetch__?: typeof fetch;
  __bflMonitoringFetchRefCount__?: number;
}

// Captures the metric ids and request attributes needed after a fetch completes.
interface MonitoredApiRequest {
  errorMetricId: string;
  responseTimeMetricId: string;
  attributes: Record<string, MonitoringPrimitive>;
}

// Normalizes the different fetch input shapes into a string URL.
function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
}

// Resolves the final HTTP method used for the fetch call.
function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  const requestMethod =
    (init?.method ??
      (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET")) ||
    "GET";

  return requestMethod.toUpperCase();
}

// Reads the current page context so request metrics can be tied to a screen.
function getRequestPageContext(): Pick<ApiRequestContext, "pagePath" | "pageUrl"> {
  if (typeof window === "undefined") {
    return {
      pagePath: "",
      pageUrl: "",
    };
  }

  return {
    pagePath: window.location.pathname,
    pageUrl: window.location.href,
  };
}

// Builds the classifier input object for the current fetch request.
function createApiRequestContext(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  requestUrl: string,
  method: string
): ApiRequestContext {
  return {
    url: requestUrl,
    method,
    input,
    init,
    ...getRequestPageContext(),
  };
}

// Precomputes the metric ids and attributes for one matched API monitor.
function createMonitorSample(
  monitor: ApiMonitorDefinition,
  requestContext: ApiRequestContext
): MonitoredApiRequest {
  const classification = monitor.classifyRequest?.(requestContext);

  return {
    errorMetricId: monitor.errorMetricId,
    responseTimeMetricId: monitor.responseTimeMetricId,
    attributes: {
      api_name: classification?.apiName ?? monitor.apiName,
      request_method: requestContext.method,
      request_page_path: requestContext.pagePath,
      request_page_url: requestContext.pageUrl,
      ...(classification?.attributes ?? {}),
    },
  };
}

// Records a successful fetch outcome using the response status code.
function recordApiSuccess(
  schema: PodMonitoringSchema,
  monitor: MonitoredApiRequest,
  duration: number,
  response: Response
): void {
  const success = response.ok;

  emitApiMetricSample(schema, monitor.responseTimeMetricId, {
    value: duration,
    unit: "ms",
    statusCode: response.status,
    success,
    percentile: 90,
    extra: {
      ...monitor.attributes,
    },
  });

  emitApiMetricSample(schema, monitor.errorMetricId, {
    value: 1,
    unit: "request",
    statusCode: response.status,
    success,
    extra: {
      ...monitor.attributes,
      is_error: !success,
    },
  });
}

// Records a failed fetch outcome and preserves the thrown error message.
function recordApiFailure(
  schema: PodMonitoringSchema,
  monitor: MonitoredApiRequest,
  duration: number,
  error: unknown
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);

  emitApiMetricSample(schema, monitor.responseTimeMetricId, {
    value: duration,
    unit: "ms",
    statusCode: 0,
    success: false,
    percentile: 90,
    extra: {
      ...monitor.attributes,
      error_message: errorMessage,
    },
  });

  emitApiMetricSample(schema, monitor.errorMetricId, {
    value: 1,
    unit: "request",
    statusCode: 0,
    success: false,
    extra: {
      ...monitor.attributes,
      is_error: true,
      error_message: errorMessage,
    },
  });
}

// Patches window.fetch so matching requests automatically emit API metrics.
export function installFetchMonitoring(schema: PodMonitoringSchema): () => void {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return () => undefined;
  }

  const globals = window as Window & FetchMonitorGlobals;
  const originalFetch = globals.__bflMonitoringOriginalFetch__ ?? window.fetch.bind(window);

  if (!globals.__bflMonitoringOriginalFetch__) {
    globals.__bflMonitoringOriginalFetch__ = originalFetch;
    globals.__bflMonitoringFetchRefCount__ = 0;
  }

  globals.__bflMonitoringFetchRefCount__ = (globals.__bflMonitoringFetchRefCount__ ?? 0) + 1;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl = getRequestUrl(input);
    const method = getRequestMethod(input, init);
    const matchingMonitors = resolveMatchingApiMonitors(schema, requestUrl, method);

    if (matchingMonitors.length === 0) {
      return originalFetch(input, init);
    }

    const requestContext = createApiRequestContext(input, init, requestUrl, method);
    const monitoredRequests = matchingMonitors.map((monitor) =>
      createMonitorSample(monitor, requestContext)
    );
    const startTime = performance.now();

    try {
      const response = await originalFetch(input, init);
      const duration = performance.now() - startTime;

      monitoredRequests.forEach((monitor) => {
        recordApiSuccess(schema, monitor, duration, response);
      });

      return response;
    } catch (error) {
      const duration = performance.now() - startTime;

      monitoredRequests.forEach((monitor) => {
        recordApiFailure(schema, monitor, duration, error);
      });

      throw error;
    }
  };

  return () => {
    const currentRefCount = (globals.__bflMonitoringFetchRefCount__ ?? 1) - 1;
    globals.__bflMonitoringFetchRefCount__ = Math.max(currentRefCount, 0);

    if (globals.__bflMonitoringFetchRefCount__ === 0 && globals.__bflMonitoringOriginalFetch__) {
      window.fetch = globals.__bflMonitoringOriginalFetch__;
      delete globals.__bflMonitoringOriginalFetch__;
      delete globals.__bflMonitoringFetchRefCount__;
    }
  };
}
